import { PaletteMode, Components, Theme } from '@mui/material';
import { themePalette } from '../theme.palette';
import { typographyTokens } from '../typography-tokens';
import dimensions from '../dimensions';

export const MuiTextFieldTheme = (mode: PaletteMode): Components<Theme>['MuiTextField'] => {
  const { componentTokens } = themePalette(mode);
  const { spacing } = dimensions;

  return {
    defaultProps: {
      variant: 'outlined',
    },
    styleOverrides: {
      root: {
        // Base styles for the TextField root
        '& .MuiInputBase-input': {
          fontSize: typographyTokens.fontSizes.sm,
          fontWeight: typographyTokens.fontWeights.regular,
          lineHeight: typographyTokens.lineHeights.sm,
          color: componentTokens.inputs.colors.normal.text.inputText.selected,
          fontFamily: typographyTokens.fontFamilies.sans,
          borderRadius: spacing(1.5), // 6px
          height: 'auto',
          '&::placeholder': {
            color: componentTokens.inputs.colors.normal.text.inputText.selected,
          },
        },
        '& textarea': {
          // Enable resizing for textarea elements
          resize: 'vertical',
          overflow: 'auto',
          padding: '0px !important',
        },
        '& .MuiInputLabel-root': {
          fontSize: typographyTokens.fontSizes.sm,
          fontWeight: typographyTokens.fontWeights.medium,
          color: componentTokens.inputs.colors.normal.text.inputText.resting,
          transition:
            'color 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms, transform 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms',
          '&:not(.Mui-disabled):hover': {
            color: componentTokens.inputs.colors.normal.text.inputText.hover,
          },
          '&.Mui-disabled': {
            opacity: 0.4,
            cursor: 'not-allowed',
          },
          '&.MuiInputLabel-shrink': {
            // Adjust the label position when it moves to the top
            transform: 'translate(14px, 8px) scale(0.65)',
            backgroundColor: 'transparent',
            color: componentTokens.inputs.colors.normal.text.label.resting,
            fontWeight: typographyTokens.fontWeights.semibold,
          },
          '&.Mui-focused': {
            color: componentTokens.inputs.colors.normal.text.inputText.selected,
          },
        },
        '& .MuiOutlinedInput-root': {
          // Outlined variant styling
          backgroundColor: componentTokens.inputs.colors.normal.background.resting,
          borderColor: componentTokens.inputs.colors.normal.stroke.resting,
          color: componentTokens.inputs.colors.normal.text.inputText.resting,
          transition:
            'border-color 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms, background-color 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms',
          '& fieldset': {
            borderColor: componentTokens.inputs.colors.normal.stroke.resting,
            transition: 'border-color 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms',
            '& legend': {
              // Hide the legend gap - we want continuous border
              maxWidth: 0,
              visibility: 'hidden',
            },
          },
          '&:not(.Mui-disabled):hover': {
            backgroundColor: componentTokens.inputs.colors.normal.background.hover,
          },
          '&:not(.Mui-disabled):hover fieldset': {
            borderColor: componentTokens.inputs.colors.normal.stroke.hover,
          },
          '&.Mui-focused': {
            backgroundColor: componentTokens.inputs.colors.normal.background.selected,
          },
          '&.Mui-focused fieldset': {
            borderColor: componentTokens.inputs.colors.normal.stroke.selected,
            borderWidth: '1px', // Override MUI's default 2px on focus
          },
          '&.Mui-focused-visible fieldset': {
            borderColor: componentTokens.inputs.colors.normal.stroke.selected,
            borderWidth: '1px',
          },
          '&.Mui-error': {
            backgroundColor: componentTokens.inputs.colors.error.background.resting,
          },
          '&.Mui-error fieldset': {
            borderColor: componentTokens.inputs.colors.error.stroke.resting,
          },
          '&.Mui-error:not(.Mui-disabled):hover': {
            backgroundColor: componentTokens.inputs.colors.error.background.hover,
          },
          '&.Mui-error:not(.Mui-disabled):hover fieldset': {
            borderColor: componentTokens.inputs.colors.error.stroke.hover,
          },
          '&.Mui-error.Mui-focused': {
            backgroundColor: componentTokens.inputs.colors.error.background.selected,
          },
          '&.Mui-error.Mui-focused fieldset': {
            borderColor: componentTokens.inputs.colors.error.stroke.selected,
            borderWidth: '1px', // Override MUI's default 2px on focus
          },
          '&.Mui-error.Mui-focused-visible fieldset': {
            borderColor: componentTokens.inputs.colors.error.stroke.selected,
            borderWidth: '1px',
          },
          '&.Mui-disabled': {
            opacity: 0.4,
            cursor: 'not-allowed',
            backgroundColor: componentTokens.inputs.colors.normal.background.resting,
            '& fieldset': {
              borderColor: componentTokens.inputs.colors.normal.stroke.resting,
            },
            '& .MuiInputBase-input': {
              color: componentTokens.inputs.colors.normal.text.inputText.resting,
              WebkitTextFillColor: componentTokens.inputs.colors.normal.text.inputText.resting,
            },
          },
        },
        // Specific padding when input has a start adornment (prefix)
        '& .MuiInputAdornment-root': {
          marginRight: '-6px',
          color: componentTokens.inputs.colors.normal.assets.input.resting,
          '& .MuiSvgIcon-root': {
            color: componentTokens.inputs.colors.normal.assets.input.resting,
          },
        },
      },
    },
  };
};
